import { ChevronDown } from "lucide-react";
import React from "react";
import "../chat-v8.css";

import {
  z_ChatV8RunMode,
  type T_ChatV8RunMode,
  type T_ChatV8RunModeOption,
} from "@lattice/core-lib/types/chat-v8";

import { DropdownMenu } from "@lattice/frontend/components/kit/DropdownMenu";
import { Pill } from "@lattice/frontend/components/kit/Pill";

export type T_ModelPickerPillProps = {
  modes: readonly T_ChatV8RunModeOption[];
  onChange: (mode: T_ChatV8RunMode) => void;
  value: T_ChatV8RunMode;
};

/**
 * Web counterpart to mobile ModelPickerPill. The backend capabilities response
 * owns the available modes and product copy; this component only renders the
 * selected scalar and reports a validated mode change.
 */
export function ModelPickerPill({
  modes,
  onChange,
  value,
}: T_ModelPickerPillProps) {
  const active = modes.find((option) => option.mode === value) ?? modes[0];
  if (!active) return null;

  return (
    <span className="chat-v8-model-picker">
      <DropdownMenu
        align="left"
        items={modes.map((option) => ({
          description: option.description,
          id: option.mode,
          label: option.label,
          selected: option.mode === active.mode,
        }))}
        menuAccessibilityLabel="Prism model options"
        menuWidth={260}
        onSelect={(item) => {
          const parsedMode = z_ChatV8RunMode.safeParse(item.id);
          if (parsedMode.success) onChange(parsedMode.data);
        }}
        renderTrigger={({ isOpen }) => (
          <Pill
            label={active.label}
            trailing={
              <ChevronDown
                aria-hidden
                className={[
                  "chat-v8-model-picker__chevron",
                  isOpen ? "chat-v8-model-picker__chevron--open" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                size={16}
                strokeWidth={2.5}
              />
            }
          />
        )}
        triggerAccessibilityLabel={`Choose Prism model. Current: ${active.label}`}
      />
    </span>
  );
}
